import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonSkeletonText,
  IonMenuButton,
  IonButtons,
  IonRefresher,
  IonRefresherContent,
  IonPopover,
  useIonRouter,
} from '@ionic/react';
import {
  people,
  peopleCircle,
  gitNetwork,
  trendingUp,
  heartOutline,
  map,
  grid,
  shieldCheckmark,
  documentText,
  chevronForward,
  logOut,
  settingsOutline,
  person,
} from 'ionicons/icons';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useDashboardStats, useRecentActivity } from '@/hooks/useDashboard';
import { useAuthStore } from '@/store/authStore';
import { useLogout } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import MemberSearchInput from '@/components/shared/MemberSearchInput';
import UpcomingEventsHero from '@/components/calendar/UpcomingEventsHero';

const DashboardPage: React.FC = () => {
  const authStore = useAuthStore();
  const { user } = authStore;
  const router = useIonRouter();
  const logoutMutation = useLogout();
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useDashboardStats();
  const { data: recentActivity, refetch: refetchActivity } = useRecentActivity();
  const [showUserPopover, setShowUserPopover] = useState<{ open: boolean; event?: Event }>({ open: false });

  const { canAny } = usePermission();
  const isAdmin = canAny(['users.view', 'audit.view']);

  const handleLogout = () => {
    setShowUserPopover({ open: false });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('tenant_slug');
    localStorage.removeItem('tenant_name');
    authStore.logout();
    logoutMutation.mutate();
  };

  const userInitials = user
    ? `${(user.first_name?.[0] ?? '').toUpperCase()}${(user.last_name?.[0] ?? '').toUpperCase()}`
    : '?';

  const userDisplayName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.name || 'Utilizator'
    : 'Utilizator';

  const handleRefresh = async (event: CustomEvent) => {
    await Promise.all([refetchStats(), refetchActivity()]);
    event.detail.complete();
  };

  const handleDashSearch = () => {
    if (dashSearch.trim()) {
      router.push(`/members?search=${encodeURIComponent(dashSearch.trim())}`, 'forward');
    } else {
      router.push('/members', 'forward');
    }
  };

  const totalMembers = stats?.total_members ?? 0;
  const activeMembers = stats?.active_members ?? 0;
  const totalFamilies = stats?.total_families ?? 0;
  const totalGroups = stats?.total_groups ?? 0;
  const newThisMonth = stats?.new_members_this_month ?? 0;
  const outreachCount = stats?.outreach_count ?? 0;
  const activePercent = totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0;

  const memberCount = stats?.members_by_type?.member ?? 0;
  const dependentCount = stats?.members_by_type?.dependent ?? 0;
  const sectorData = stats?.members_by_sector ?? [];
  const [dashSearch, setDashSearch] = useState('');

  const navItems = [
    { title: 'Membri', subtitle: `${totalMembers} persoane`, path: '/members', icon: people, color: '#5e81f4', bg: '#eef2ff' },
    { title: 'Familii', subtitle: `${totalFamilies} familii`, path: '/families', icon: peopleCircle, color: '#9698d6', bg: '#f5f3ff' },
    { title: 'Grupuri', subtitle: `${totalGroups} grupuri`, path: '/groups', icon: gitNetwork, color: '#7ce7ac', bg: '#ecfdf5' },
    { title: 'Hartă', subtitle: 'Vizualizare sector', path: '/maps', icon: map, color: '#40e1fa', bg: '#ecfeff' },
    { title: 'Sectoare', subtitle: '8 sectoare', path: '/sectors', icon: grid, color: '#f4be5e', bg: '#fffbeb' },
    ...(isAdmin ? [
      { title: 'Utilizatori', subtitle: 'Administrare', path: '/admin/users', icon: shieldCheckmark, color: '#ff808b', bg: '#fef2f2' },
      { title: 'Jurnal Audit', subtitle: 'Istoric acțiuni', path: '/admin/audit', icon: documentText, color: '#8181a5', bg: '#f5f5fa' },
    ] : []),
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton style={{ '--color': '#8181a5' }} />
          </IonButtons>
          <IonTitle>Panou de Control</IonTitle>
          <IonButtons slot="end">
            <button className="crm-toolbar-icon" onClick={(e) => setShowUserPopover({ open: true, event: e.nativeEvent })}>
              <IonIcon icon={settingsOutline} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {/* User popover with logout */}
      <IonPopover
        isOpen={showUserPopover.open}
        event={showUserPopover.event}
        onDidDismiss={() => setShowUserPopover({ open: false })}
        side="bottom"
        alignment="end"
        style={{ '--width': '260px' }}
      >
        <div style={{ background: '#ffffff' }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #ececf2',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: '#5e81f4', color: '#ffffff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14,
              flexShrink: 0,
            }}>
              {userInitials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: '#1c1d21' }}>
                {userDisplayName}
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#8181a5', textTransform: 'capitalize' }}>
                {user?.role?.replace('_', ' ') ?? ''}
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#8181a5', marginTop: 2 }}>
                {user?.email ?? ''}
              </div>
            </div>
          </div>
          <div style={{ padding: '10px 12px' }}>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#fef2f2',
                color: '#ff808b',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#fee2e2'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#fef2f2'; }}
            >
              <IonIcon icon={logOut} style={{ fontSize: 18 }} />
              Deconectare
            </button>
          </div>
        </div>
      </IonPopover>

      <IonContent style={{ '--background': '#f5f5fa' }}>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="dash-wrap">
          {/* Greeting */}
          <div className="dash-greeting">
            <p className="dash-greeting__hello">Bine ai venit,</p>
            <h2 className="dash-greeting__name">{user?.first_name?.toUpperCase() ?? 'UTILIZATOR'}</h2>
            <p className="dash-greeting__date">{format(new Date(), 'EEEE, d MMMM yyyy', { locale: ro })}</p>
          </div>

          {/* ── HERO Search Bar ── */}
          <MemberSearchInput
            variant="hero"
            placeholder="Caută persoane după nume, telefon, email..."
            value={dashSearch}
            onChange={setDashSearch}
            onSubmit={handleDashSearch}
            onSelect={(m) => router.push(`/members/${m.id}`, 'forward')}
          />

          <UpcomingEventsHero />

          {/* ── Hero People Cards — primary navigation ── */}
          <div className="dash-hero-grid">
            {statsLoading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="dash-hero-card">
                  <IonSkeletonText animated style={{ width: 60, height: 36 }} />
                  <IonSkeletonText animated style={{ width: 100, height: 18, marginTop: 8 }} />
                </div>
              ))
            ) : (
              <>
                <div className="dash-hero-card dash-hero-card--membri" onClick={() => router.push('/members?membership_type=member', 'forward')}>
                  <div className="dash-hero-card__icon-wrap">
                    <IonIcon icon={people} />
                  </div>
                  <div className="dash-hero-card__count">{memberCount.toLocaleString('ro-RO')}</div>
                  <div className="dash-hero-card__label">Membri</div>
                  <IonIcon icon={chevronForward} className="dash-hero-card__arrow" />
                </div>

                <div className="dash-hero-card dash-hero-card--apartin" onClick={() => router.push('/members?membership_type=dependent', 'forward')}>
                  <div className="dash-hero-card__icon-wrap">
                    <IonIcon icon={person} />
                  </div>
                  <div className="dash-hero-card__count">{dependentCount.toLocaleString('ro-RO')}</div>
                  <div className="dash-hero-card__label">Aparținători</div>
                  <IonIcon icon={chevronForward} className="dash-hero-card__arrow" />
                </div>

                <div className="dash-hero-card dash-hero-card--total" onClick={() => router.push('/members', 'forward')}>
                  <div className="dash-hero-card__icon-wrap">
                    <IonIcon icon={people} />
                  </div>
                  <div className="dash-hero-card__count">{totalMembers.toLocaleString('ro-RO')}</div>
                  <div className="dash-hero-card__label">Toți</div>
                  <IonIcon icon={chevronForward} className="dash-hero-card__arrow" />
                </div>
              </>
            )}
          </div>

          {/* ── Secondary stat row — Familii, Grupuri, Noi luna aceasta ── */}
          <div className="dash-stats-grid">
            {!statsLoading && (
              <>
                <div className="dash-stat-card" onClick={() => router.push('/families', 'forward')}>
                  <div>
                    <div className="dash-stat-card__value">{totalFamilies.toLocaleString('ro-RO')}</div>
                    <div className="dash-stat-card__label">Familii</div>
                  </div>
                  <div className="dash-stat-card__icon" style={{ background: '#f5f3ff' }}>
                    <IonIcon icon={peopleCircle} style={{ fontSize: 22, color: '#9698d6' }} />
                  </div>
                </div>

                <div className="dash-stat-card" onClick={() => router.push('/groups', 'forward')}>
                  <div>
                    <div className="dash-stat-card__value">{totalGroups}</div>
                    <div className="dash-stat-card__label">Grupuri Active</div>
                  </div>
                  <div className="dash-stat-card__icon" style={{ background: '#ecfdf5' }}>
                    <IonIcon icon={gitNetwork} style={{ fontSize: 22, color: '#7ce7ac' }} />
                  </div>
                </div>

                {outreachCount > 0 && (
                  <div className="dash-stat-card" onClick={() => router.push('/nebotezati', 'forward')}>
                    <div>
                      <div className="dash-stat-card__value">{outreachCount}</div>
                      <div className="dash-stat-card__label">De contactat</div>
                    </div>
                    <div className="dash-stat-card__icon" style={{ background: '#fef2f2' }}>
                      <IonIcon icon={heartOutline} style={{ fontSize: 22, color: '#ef4444' }} />
                    </div>
                  </div>
                )}

                <div className="dash-stat-card">
                  <div>
                    <div className="dash-stat-card__value">{newThisMonth}</div>
                    <div className="dash-stat-card__label">Noi luna aceasta</div>
                  </div>
                  <div className="dash-stat-card__icon" style={{ background: '#fffbeb' }}>
                    <IonIcon icon={trendingUp} style={{ fontSize: 22, color: '#f4be5e' }} />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Quick Navigation Grid ── */}
          <div className="dash-nav">
            <h4 className="dash-nav__title">Navigare rapidă</h4>
            <div className="dash-nav__grid">
              {navItems.map((item) => (
                <div key={item.path} className="dash-nav__item" onClick={() => router.push(item.path, 'forward')}>
                  <div className="dash-nav__item-icon" style={{ background: item.bg }}>
                    <IonIcon icon={item.icon} style={{ fontSize: 20, color: item.color }} />
                  </div>
                  <div className="dash-nav__item-text">
                    <div className="dash-nav__item-title">{item.title}</div>
                    <div className="dash-nav__item-sub">{item.subtitle}</div>
                  </div>
                  <IonIcon icon={chevronForward} style={{ fontSize: 16, color: '#ececf2', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>

          {/* ── Stats Overview ── */}
          <div className="dash-stats-panel">
            <div className="dash-stats-panel__header">
              <h4 className="dash-stats-panel__title">Statistici</h4>
            </div>
            <div className="dash-stats-panel__body">
              {/* Active members bar */}
              <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f3' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: '#1c1d21' }}>Membri Activi</span>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: '#5e81f4' }}>{activePercent}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: '#ececf2', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 3, background: '#5e81f4', width: `${activePercent}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>

              {/* Sector Distribution */}
              {isAdmin && sectorData.length > 0 && (
                <div style={{ paddingTop: 14 }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: '#1c1d21', marginBottom: 12 }}>
                    Membri pe Sector
                  </div>
                  {sectorData.map((sector: any) => (
                    <div key={sector.sector_name} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 0', borderBottom: '1px solid #f5f5fa',
                    }}>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: '#1c1d21' }}>
                        {sector.sector_name}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: Math.min(totalMembers > 0 ? (sector.count / totalMembers) * 120 : 0, 120),
                          height: 6, borderRadius: 3, background: '#5e81f4',
                          transition: 'width 0.4s ease',
                        }} />
                        <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: '#1c1d21', minWidth: 30, textAlign: 'right' }}>
                          {sector.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!statsLoading && !stats && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8181a5', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                  Nu sunt date disponibile
                </div>
              )}
            </div>
          </div>

          {/* ── Recent Activity — minimal, at very bottom ── */}
          <div className="dash-activity">
            <div className="dash-activity__header">
              <h4 className="dash-activity__title">Activitate Recentă</h4>
            </div>
            <div className="dash-activity__body">
              {recentActivity && recentActivity.length > 0 ? (
                recentActivity.slice(0, 4).map((activity: any) => (
                  <div key={activity.id} className="dash-activity__row">
                    <div className="dash-activity__avatar">
                      {(activity.user_name?.[0] ?? 'A').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="dash-activity__desc">{activity.description}</div>
                      <div className="dash-activity__user">{activity.user_name}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#b0b0c8', fontFamily: 'Inter, sans-serif', fontSize: 12 }}>
                  Nicio activitate recentă
                </div>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default DashboardPage;
